import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '../context/AuthContext';
import { 
  Search, Users, GraduationCap, Filter, Download, 
  TrendingUp, TrendingDown, Minus, BarChart3, PieChart,
  Sparkles, Brain, Target, Award, Clock, Zap, ChevronRight,
  BookOpen, Mic, Volume2, Video, FileText, X, RefreshCw,
  PenLine, Clapperboard, Puzzle, Loader2, Trophy, Gamepad2, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================
// Students List Sheet Component
// ============================================
export const StudentsListSheet = ({ open, onOpenChange, isTeacher = false }) => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentStats, setStudentStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsRes, classesRes] = await Promise.all([
        api.get('/users?role=student'),
        api.get('/classes')
      ]);
      setStudents(studentsRes.data);
      setClasses(classesRes.data);
    } catch (err) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      const matchesSearch = 
        student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.student_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesClass = selectedClass === 'all' || student.class_id === selectedClass;
      
      return matchesSearch && matchesClass;
    });
  }, [students, searchTerm, selectedClass]);

  const classStats = useMemo(() => {
    const stats = {};
    students.forEach(s => {
      const classId = s.class_id || 'unassigned';
      stats[classId] = (stats[classId] || 0) + 1;
    });
    return stats;
  }, [students]);

  const handleStudentClick = async (student) => {
    setSelectedStudent(student);
    setStatsDialogOpen(true);
    setStatsLoading(true);
    try {
      const response = await api.get(`/students/${student.id}/statistics`);
      setStudentStats(response.data);
    } catch (err) {
      toast.error('Failed to load student statistics');
      setStudentStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const moduleConfig = {
    tts: { icon: Volume2, name: 'Text to Speech', color: 'bg-cyan-500', bgColor: 'from-cyan-900/50 to-cyan-950/50' },
    stt: { icon: MessageSquare, name: 'Speech to Text', color: 'bg-emerald-500', bgColor: 'from-emerald-900/50 to-emerald-950/50' },
    mcq: { icon: FileText, name: 'MCQ Quiz', color: 'bg-violet-500', bgColor: 'from-violet-900/50 to-violet-950/50' },
    vmcq: { icon: Clapperboard, name: 'Cinematic MCQ', color: 'bg-rose-500', bgColor: 'from-rose-900/50 to-rose-950/50' },
    ftb: { icon: PenLine, name: 'Fill in Blanks', color: 'bg-amber-500', bgColor: 'from-amber-900/50 to-amber-950/50' }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl bg-slate-900/95 backdrop-blur-xl border-white/10">
        <SheetHeader className="border-b border-white/10 pb-4">
          <SheetTitle className="text-xl text-white flex items-center gap-2">
            <div className="p-2 rounded-lg bg-indigo-500/20">
              <Users className="h-5 w-5 text-indigo-400" />
            </div>
            {isTeacher ? 'My Students' : 'All Students'}
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            {filteredStudents.length} of {students.length} students shown
          </SheetDescription>
        </SheetHeader>

        <div className="py-4 space-y-4">
          {/* Filters */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, ID, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
              />
            </div>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="All Classes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {classes.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} ({classStats[c.id] || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* AI Insights Banner */}
          <div className="p-3 rounded-xl bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-violet-400" />
              <span className="text-sm font-medium text-violet-300">AI Insights</span>
            </div>
            <p className="text-xs text-slate-300">
              {students.length > 0 
                ? `${classes.length} classes with an average of ${Math.round(students.length / (classes.length || 1))} students per class.`
                : 'Add students to see insights.'
              }
            </p>
          </div>

          {/* Students List */}
          <ScrollArea className="h-[calc(100vh-320px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-violet-400" />
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No students found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredStudents.map((student, idx) => (
                  <div 
                    key={student.id}
                    data-testid={`student-row-${student.student_id || student.id}`}
                    className="p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all border border-transparent hover:border-violet-500/30 group cursor-pointer"
                    onClick={() => handleStudentClick(student)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
                          {student.name?.charAt(0) || 'S'}
                        </div>
                        <div>
                          <p className="font-medium text-white group-hover:text-violet-300 transition-colors">
                            {student.name}
                          </p>
                          <p className="text-xs text-slate-400">
                            {student.student_id || student.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-white/10 text-slate-300">
                          {classes.find(c => c.id === student.class_id)?.name || 'No Class'}
                        </Badge>
                        <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-violet-400 transition-colors" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Quick Stats Footer */}
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10">
            <div className="p-2 rounded-lg bg-white/5 text-center">
              <p className="text-lg font-bold text-white">{filteredStudents.length}</p>
              <p className="text-xs text-slate-400">Shown</p>
            </div>
            <div className="p-2 rounded-lg bg-white/5 text-center">
              <p className="text-lg font-bold text-cyan-400">{classes.length}</p>
              <p className="text-xs text-slate-400">Classes</p>
            </div>
            <div className="p-2 rounded-lg bg-white/5 text-center">
              <p className="text-lg font-bold text-violet-400">{students.length}</p>
              <p className="text-xs text-slate-400">Total</p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    {/* Student Analytics Dialog */}
    <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700 text-white p-0" data-testid="student-analytics-dialog">
        {statsLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-indigo-500" />
          </div>
        ) : studentStats ? (
          <>
            {/* Header */}
            <div className="sticky top-0 z-10 bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-600 p-6 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-2xl font-bold text-white">
                    {studentStats.student_info?.name?.charAt(0) || 'S'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white" data-testid="student-analytics-name">
                      {studentStats.student_info?.name}
                    </h2>
                    <p className="text-white/80 text-sm">
                      ID: {studentStats.student_info?.student_id} | Class: {studentStats.student_info?.class_name || 'N/A'} | Roll: {studentStats.student_info?.roll_no || '-'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setStatsDialogOpen(false)}
                  className="text-white/80 hover:text-white hover:bg-white/20"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg">
                  <Target className="h-8 w-8 text-blue-200 mb-2" />
                  <p className="text-2xl font-bold text-white">{studentStats.totals?.total_attempts}</p>
                  <p className="text-sm text-white/80">Total Attempts</p>
                </div>
                <div className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-lg">
                  <Trophy className="h-8 w-8 text-emerald-200 mb-2" />
                  <p className="text-2xl font-bold text-white">{studentStats.totals?.module_score}</p>
                  <p className="text-sm text-white/80">Module Score</p>
                </div>
                <div className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-amber-500 to-amber-700 shadow-lg">
                  <Gamepad2 className="h-8 w-8 text-amber-200 mb-2" />
                  <p className="text-2xl font-bold text-white">{studentStats.totals?.fun_learning_score}</p>
                  <p className="text-sm text-white/80">Fun Learning</p>
                </div>
                <div className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-pink-500 to-pink-700 shadow-lg">
                  <Award className="h-8 w-8 text-pink-200 mb-2" />
                  <p className="text-2xl font-bold text-white">{studentStats.totals?.total_score}</p>
                  <p className="text-sm text-white/80">Total Score</p>
                </div>
              </div>

              {/* Module Performance */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-400" />
                  Module Performance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {Object.entries(moduleConfig).map(([key, config]) => {
                    const stats = studentStats.module_stats?.[key] || { attempts: 0, total_score: 0, avg_score: 0, best_score: 0 };
                    const Icon = config.icon;
                    return (
                      <div key={key} data-testid={`student-module-${key}`} className={`bg-gradient-to-br ${config.bgColor} rounded-xl p-4 border border-white/10`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`p-2 rounded-lg ${config.color}`}>
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                          <h4 className="font-semibold text-white text-sm">{config.name}</h4>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-white/5 rounded-lg p-2 text-center">
                            <p className="text-lg font-bold text-cyan-400">{stats.attempts}</p>
                            <p className="text-xs text-slate-400">Attempts</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2 text-center">
                            <p className="text-lg font-bold text-emerald-400">{stats.avg_score}%</p>
                            <p className="text-xs text-slate-400">Avg Score</p>
                          </div>
                        </div>
                        <div className="mt-2 flex justify-between text-xs text-slate-400">
                          <span>Best: {stats.best_score}</span>
                          <span>Total: {stats.total_score}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Fun Learning */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Gamepad2 className="h-5 w-5 text-pink-400" />
                  Fun Learning Games
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'picture_match', icon: Puzzle, name: 'Picture Match', color: 'text-cyan-400' },
                    { key: 'context_word', icon: BookOpen, name: 'Context Word', color: 'text-violet-400' },
                    { key: 'spelling_bee', icon: Sparkles, name: 'Spelling Bee', color: 'text-amber-400' },
                    { key: 'sentence_builder', icon: FileText, name: 'Sentence Builder', color: 'text-emerald-400' }
                  ].map(game => {
                    const gStats = studentStats.fun_learning_stats?.[game.key] || { score: 0, plays: 0 };
                    const GameIcon = game.icon;
                    return (
                      <div key={game.key} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-3 border border-white/5">
                        <div className="flex items-center gap-2 mb-2">
                          <GameIcon className={`h-4 w-4 ${game.color}`} />
                          <span className="text-sm font-medium text-white">{game.name}</span>
                        </div>
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-lg font-bold text-white">{gStats.score}</p>
                            <p className="text-xs text-slate-400">Score</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-emerald-400">{gStats.plays}</p>
                            <p className="text-xs text-slate-400">Plays</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Activity */}
              {studentStats.recent_activity?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-cyan-400" />
                    Recent Activity
                  </h3>
                  <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl overflow-hidden border border-white/10">
                    <div className="divide-y divide-white/5">
                      {studentStats.recent_activity.map((activity, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 hover:bg-white/5">
                          <div className="flex items-center gap-3">
                            <Badge className="bg-white/10 text-slate-300 uppercase text-xs">{activity.module_type}</Badge>
                            <span className="text-sm text-slate-300">{new Date(activity.created_at).toLocaleDateString()}</span>
                          </div>
                          <span className="text-sm font-semibold text-emerald-400">{activity.score} pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="py-20 text-center text-slate-400">
            <p>Failed to load student statistics</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

// ============================================
// Analytics Dialog Component
// ============================================
export const AnalyticsDialog = ({ open, onOpenChange, title = "Analytics" }) => {
  const [stats, setStats] = useState(null);
  const [moduleStats, setModuleStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchAnalytics();
    }
  }, [open]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, statsRes] = await Promise.all([
        api.get('/statistics/overview'),
        api.get('/statistics?period=monthly')
      ]);
      setStats(overviewRes.data);
      setModuleStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const totalAttempts = moduleStats.reduce((sum, m) => sum + (m.total_attempts || 0), 0);

  const moduleColors = {
    tts: { bg: 'bg-cyan-500', text: 'text-cyan-400', label: 'Text to Speech' },
    stt: { bg: 'bg-emerald-500', text: 'text-emerald-400', label: 'Speech to Text' },
    mcq: { bg: 'bg-amber-500', text: 'text-amber-400', label: 'MCQ Quiz' },
    vmcq: { bg: 'bg-purple-500', text: 'text-purple-400', label: 'Video MCQ' }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-slate-900/95 backdrop-blur-xl border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <div className="p-2 rounded-lg bg-rose-500/20">
              <BarChart3 className="h-5 w-5 text-rose-400" />
            </div>
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Comprehensive performance analytics and insights
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-violet-400" />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="mt-4">
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger value="overview" className="data-[state=active]:bg-violet-500/20">
                Overview
              </TabsTrigger>
              <TabsTrigger value="modules" className="data-[state=active]:bg-violet-500/20">
                By Module
              </TabsTrigger>
              <TabsTrigger value="insights" className="data-[state=active]:bg-violet-500/20">
                AI Insights
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              {/* Visual Chart - CSS Based Pie Chart */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Attempts by Module</h4>
                  <div className="relative w-32 h-32 mx-auto">
                    {/* CSS Pie Chart */}
                    <div 
                      className="w-full h-full rounded-full"
                      style={{
                        background: totalAttempts > 0 
                          ? `conic-gradient(
                              #06b6d4 0deg ${(moduleStats.find(m => m.module_type === 'tts')?.total_attempts || 0) / totalAttempts * 360}deg,
                              #10b981 ${(moduleStats.find(m => m.module_type === 'tts')?.total_attempts || 0) / totalAttempts * 360}deg ${((moduleStats.find(m => m.module_type === 'tts')?.total_attempts || 0) + (moduleStats.find(m => m.module_type === 'stt')?.total_attempts || 0)) / totalAttempts * 360}deg,
                              #f59e0b ${((moduleStats.find(m => m.module_type === 'tts')?.total_attempts || 0) + (moduleStats.find(m => m.module_type === 'stt')?.total_attempts || 0)) / totalAttempts * 360}deg 360deg
                            )`
                          : '#374151'
                      }}
                    />
                    <div className="absolute inset-4 rounded-full bg-slate-900 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{totalAttempts}</p>
                        <p className="text-xs text-slate-400">Total</p>
                      </div>
                    </div>
                  </div>
                  {/* Legend */}
                  <div className="mt-4 space-y-1">
                    {moduleStats.map(m => (
                      <div key={m.module_type} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${moduleColors[m.module_type]?.bg || 'bg-slate-500'}`} />
                          <span className="text-slate-300">{moduleColors[m.module_type]?.label || m.module_type}</span>
                        </div>
                        <span className="text-white font-medium">{m.total_attempts || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Performance Metrics</h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Average Score</span>
                        <span className="text-white font-medium">{stats?.average_score || 0}%</span>
                      </div>
                      <Progress value={stats?.average_score || 0} className="h-2 bg-white/10" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 rounded-lg bg-white/5 text-center">
                        <p className="text-lg font-bold text-emerald-400">{stats?.total_students || 0}</p>
                        <p className="text-xs text-slate-400">Students</p>
                      </div>
                      <div className="p-2 rounded-lg bg-white/5 text-center">
                        <p className="text-lg font-bold text-cyan-400">{stats?.total_questions || 0}</p>
                        <p className="text-xs text-slate-400">Questions</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats Row */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Teachers', value: stats?.total_teachers || 0, color: 'text-sky-400' },
                  { label: 'Classes', value: stats?.total_classes || 0, color: 'text-indigo-400' },
                  { label: 'Questions', value: stats?.total_questions || 0, color: 'text-orange-400' },
                  { label: 'Avg Score', value: `${stats?.average_score || 0}%`, color: 'text-purple-400' },
                ].map((item, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-white/5 text-center border border-white/5">
                    <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
                    <p className="text-xs text-slate-400">{item.label}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="modules" className="mt-4 space-y-3">
              {moduleStats.length === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No module data available yet</p>
                </div>
              ) : (
                moduleStats.map((module, idx) => {
                  const config = moduleColors[module.module_type] || { bg: 'bg-slate-500', text: 'text-slate-400', label: module.module_type };
                  const icon = module.module_type === 'tts' ? Volume2 : 
                               module.module_type === 'stt' ? Mic : 
                               module.module_type === 'vmcq' ? Video : BookOpen;
                  const Icon = icon;
                  
                  return (
                    <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-violet-500/30 transition-all">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${config.bg}/20`}>
                            <Icon className={`h-5 w-5 ${config.text}`} />
                          </div>
                          <div>
                            <p className="font-medium text-white">{config.label}</p>
                            <p className="text-xs text-slate-400">{module.total_attempts || 0} attempts</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${config.text}`}>
                            {Math.round(module.average_score || 0)}%
                          </p>
                          <p className="text-xs text-slate-400">avg score</p>
                        </div>
                      </div>
                      <Progress 
                        value={module.average_score || 0} 
                        className="h-1.5 bg-white/10"
                      />
                    </div>
                  );
                })
              )}
            </TabsContent>

            <TabsContent value="insights" className="mt-4 space-y-4">
              <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border border-violet-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="h-5 w-5 text-violet-400" />
                  <span className="font-medium text-violet-300">AI Performance Analysis</span>
                </div>
                <div className="space-y-3">
                  {totalAttempts > 0 ? (
                    <>
                      <div className="flex items-start gap-2">
                        <Sparkles className="h-4 w-4 text-cyan-400 mt-0.5" />
                        <p className="text-sm text-slate-300">
                          {stats?.average_score >= 70 
                            ? "Students are performing well overall. Consider introducing more advanced content."
                            : stats?.average_score >= 50
                            ? "Moderate performance detected. Focus on areas needing improvement."
                            : "Performance needs attention. Consider reviewing teaching methods and content difficulty."
                          }
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Target className="h-4 w-4 text-emerald-400 mt-0.5" />
                        <p className="text-sm text-slate-300">
                          {moduleStats.find(m => m.module_type === 'tts')?.total_attempts > moduleStats.find(m => m.module_type === 'stt')?.total_attempts
                            ? "TTS module is most engaged. Students enjoy reading practice."
                            : "Consider adding more TTS questions to boost engagement."
                          }
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Zap className="h-4 w-4 text-amber-400 mt-0.5" />
                        <p className="text-sm text-slate-300">
                          Recommendation: Schedule regular practice sessions to maintain learning momentum.
                        </p>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-400">
                      No attempts recorded yet. Insights will appear once students start taking tests.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-300">Strengths</span>
                  </div>
                  <p className="text-xs text-slate-300">
                    {moduleStats.length > 0 
                      ? `Best in: ${moduleColors[moduleStats.sort((a, b) => (b.average_score || 0) - (a.average_score || 0))[0]?.module_type]?.label || 'N/A'}`
                      : 'Data will appear after attempts'
                    }
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="h-4 w-4 text-rose-400" />
                    <span className="text-sm font-medium text-rose-300">Needs Work</span>
                  </div>
                  <p className="text-xs text-slate-300">
                    {moduleStats.length > 0 
                      ? `Focus on: ${moduleColors[moduleStats.sort((a, b) => (a.average_score || 0) - (b.average_score || 0))[0]?.module_type]?.label || 'N/A'}`
                      : 'Data will appear after attempts'
                    }
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// Questions Breakdown Dialog
// ============================================
export const QuestionsDialog = ({ open, onOpenChange }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchQuestions();
    }
  }, [open]);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/questions');
      setQuestions(res.data);
    } catch (err) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const questionsByType = useMemo(() => {
    const grouped = { tts: [], stt: [], mcq: [], vmcq: [] };
    questions.forEach(q => {
      const type = q.module_type || q.type || 'mcq';
      if (grouped[type]) {
        grouped[type].push(q);
      }
    });
    return grouped;
  }, [questions]);

  const moduleConfig = {
    tts: { icon: Volume2, color: 'text-cyan-400', bg: 'bg-cyan-500/20', label: 'Text to Speech' },
    stt: { icon: Mic, color: 'text-emerald-400', bg: 'bg-emerald-500/20', label: 'Speech to Text' },
    mcq: { icon: BookOpen, color: 'text-amber-400', bg: 'bg-amber-500/20', label: 'MCQ Quiz' },
    vmcq: { icon: Video, color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Video MCQ' }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-slate-900/95 backdrop-blur-xl border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <div className="p-2 rounded-lg bg-orange-500/20">
              <FileText className="h-5 w-5 text-orange-400" />
            </div>
            Questions Overview
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {questions.length} total questions across all modules
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-violet-400" />
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {Object.entries(questionsByType).map(([type, qs]) => {
              const config = moduleConfig[type];
              const Icon = config.icon;
              const percentage = questions.length > 0 ? Math.round((qs.length / questions.length) * 100) : 0;
              
              return (
                <div 
                  key={type}
                  className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-violet-500/30 transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.bg}`}>
                        <Icon className={`h-5 w-5 ${config.color}`} />
                      </div>
                      <div>
                        <p className="font-medium text-white">{config.label}</p>
                        <p className="text-xs text-slate-400">{percentage}% of total</p>
                      </div>
                    </div>
                    <p className={`text-2xl font-bold ${config.color}`}>{qs.length}</p>
                  </div>
                  <Progress value={percentage} className="h-1.5 bg-white/10" />
                </div>
              );
            })}

            {/* AI Suggestion */}
            <div className="p-3 rounded-lg bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border border-violet-500/20">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4 text-violet-400" />
                <span className="text-sm font-medium text-violet-300">AI Suggestion</span>
              </div>
              <p className="text-xs text-slate-300">
                {Object.entries(questionsByType).sort((a, b) => a[1].length - b[1].length)[0]?.[1]?.length === 0
                  ? `Consider adding questions to all module types for balanced learning.`
                  : `Add more ${moduleConfig[Object.entries(questionsByType).sort((a, b) => a[1].length - b[1].length)[0]?.[0]]?.label} questions to balance content.`
                }
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// Classes Overview Sheet
// ============================================
export const ClassesOverviewSheet = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [classesRes, studentsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/users?role=student')
      ]);
      setClasses(classesRes.data);
      setStudents(studentsRes.data);
    } catch (err) {
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const getStudentCount = (classId) => {
    return students.filter(s => s.class_id === classId).length;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg bg-slate-900/95 backdrop-blur-xl border-white/10">
        <SheetHeader className="border-b border-white/10 pb-4">
          <SheetTitle className="text-xl text-white flex items-center gap-2">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <GraduationCap className="h-5 w-5 text-emerald-400" />
            </div>
            Classes Overview
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            {classes.length} classes registered
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-200px)] mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-violet-400" />
            </div>
          ) : classes.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="h-12 w-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No classes created yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {classes.map((cls, idx) => {
                const studentCount = getStudentCount(cls.id);
                return (
                  <div 
                    key={cls.id}
                    onClick={() => {
                      onOpenChange(false);
                      navigate(`/teacher/my-classes/${cls.id}`);
                    }}
                    className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-all group cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 flex items-center justify-center border border-emerald-500/20">
                          <span className="text-lg font-bold text-emerald-400">{cls.name}</span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{cls.name}</p>
                          <p className="text-xs text-slate-400">{studentCount} students</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="secondary" 
                          className={`${studentCount > 30 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}
                        >
                          {studentCount > 30 ? 'Full' : studentCount > 0 ? 'Active' : 'Empty'}
                        </Badge>
                        <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-emerald-400 transition-colors" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default {
  StudentsListSheet,
  AnalyticsDialog,
  QuestionsDialog,
  ClassesOverviewSheet
};
